import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  Bell,
  ChevronDown,
  FileDown,
  History,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  PanelRight,
  PanelRightClose,
  SquarePen,
  Trash2,
  Check,
  X,
} from 'lucide-react';
import { useAgentActivity } from 'features/activity/api';
import { sessionLabel } from 'features/activity/sessionLabel';
import AgentAvatar, { agentStatus, STATUS_LABELS } from 'features/agents/AgentAvatar';
import { relativeTime } from 'features/initiatives/initiativePresentation';
import DraftPreviewControl from 'features/sandbox/components/DraftPreviewControl';
import { agentName } from 'shared/branding';
import Conversation from './Conversation';
import { useAgentPanel } from './AgentPanelContext';
import { useCanMountChat } from './useCanMountChat';
import FloatingConversation from './FloatingConversation';
import AgentLlmQuickSelect from 'features/automation/AgentLlmQuickSelect';
import { useSandboxSession } from 'features/sandbox/SandboxSessionContext';

export const AGENT_PANEL_WIDTH = 560;
const MOUNT_STORAGE_KEY = 'main_agent_chat_mounted_v1';

const readMounted = () => {
  try { return window.localStorage.getItem(MOUNT_STORAGE_KEY) === 'true'; } catch { return false; }
};

function useDismiss(open, onClose, overlayRef = null) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const handle = (event) => {
      if (ref.current && !ref.current.contains(event.target) && !overlayRef?.current?.contains(event.target)) onClose();
    };
    const escape = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('mousedown', handle);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('keydown', escape);
    };
  }, [open, onClose, overlayRef]);
  return ref;
}

const liveActivity = (panel, agent) => panel.feed?.agents?.[agent?.id] || agent?.activity || null;

/** The selected agent's running conversations the viewer may open, from Home's activity feed. */
function useLiveSessions(agentId) {
  const activity = useAgentActivity();
  const card = (activity.data?.agents || []).find((item) => String(item.agent_id) === String(agentId));
  return card && Number(card.running_count) > 0 ? card.readable_sessions || [] : [];
}

/** When the agent is working in another conversation, say where and open it here. */
function LiveElsewhere({ sessions }) {
  const panel = useAgentPanel();
  const others = sessions.filter((session) => String(session.session_id) !== String(panel.sessionId));
  if (!others.length) return null;
  const [first] = others;
  return (
    <button
      type="button"
      onClick={() => panel.openSession(panel.agent.id, first.session_id, { focus: true })}
      className="flex w-full shrink-0 items-center gap-2 border-b border-cyan-100 bg-cyan-50 px-3 py-1.5 text-left text-xs font-medium text-cyan-800 hover:bg-cyan-100"
      data-testid="live-elsewhere"
    >
      <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-cyan-500" />
      <span className="min-w-0 flex-1 truncate">Working in {sessionLabel(first)}{others.length > 1 ? ` and ${others.length - 1} more` : ''}</span>
      <span className="shrink-0">Open</span>
    </button>
  );
}

export function AgentStatusLine({ agent, activity }) {
  const status = agentStatus(agent, activity);
  if (status === 'working') {
    return (
      <span className="flex min-w-0 items-center gap-1.5 text-[11px] text-cyan-700">
        <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-cyan-500" />
        <span className="truncate">Working{activity?.current ? ` · ${activity.current}` : ''}</span>
      </span>
    );
  }
  if (status === 'waiting_on_you') {
    return (
      <span className="flex items-center gap-1.5 text-[11px] text-amber-700">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        {activity?.waiting_on_you > 1 ? `${activity.waiting_on_you} waiting on you` : 'Waiting on you'}
      </span>
    );
  }
  return <span className="text-[11px] text-slate-400">{agent?.title || STATUS_LABELS[status] || 'Available'}</span>;
}

function AgentSwitcher() {
  const panel = useAgentPanel();
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState({ top: 16, left: 16 });
  const overlayRef = useRef(null);
  const ref = useDismiss(open, () => setOpen(false), overlayRef);
  const { agent, agents, selectAgent } = panel;
  const activity = liveActivity(panel, agent);
  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const bounds = ref.current?.getBoundingClientRect();
      setPlacement({ top: (bounds?.bottom || 16) + 6, left: Math.max(8, Math.min(bounds?.left || 16, window.innerWidth - 288)) });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open, ref]);
  const hasActivity = ['working', 'waiting_on_you'].includes(agentStatus(agent, activity));
  return (
    <div className="relative min-w-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        disabled={agents.length < 2}
        className="flex max-w-full items-center gap-1 rounded-lg text-left text-sm font-semibold leading-tight text-gray-800 enabled:hover:bg-gray-100 enabled:hover:text-sky-700"
        aria-label={`Select agent: ${agentName(agent)}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="agent-switcher"
      >
        <span className="truncate">{agentName(agent)}</span>
        {agents.length > 1 ? <ChevronDown size={13} className="shrink-0 text-gray-400" /> : null}
      </button>
      {hasActivity ? <AgentStatusLine agent={agent} activity={activity} /> : null}
      {open && agents.length > 1 ? createPortal(
        <div ref={overlayRef} style={placement} className="fixed z-[60] w-[280px] max-w-[calc(100vw-16px)] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl ring-1 ring-black/5">
          <ul role="listbox" aria-label="Agents" className="max-h-72 overflow-y-auto p-1.5">
          {agents.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                role="option"
                aria-selected={item.id === agent?.id}
                onClick={() => {
                  selectAgent(item.id);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${item.id === agent?.id ? 'bg-sky-50 text-sky-900 ring-1 ring-sky-200' : 'text-gray-800 hover:bg-gray-50'}`}
              >
                <AgentAvatar ui={item.ui} size="sm" status={agentStatus(item, liveActivity(panel, item))} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-900">{agentName(item)}</span>
                  <AgentStatusLine agent={item} activity={liveActivity(panel, item)} />
                </span>
                {panel.unreadAgentIds?.has(String(item.id)) ? <span className="h-2 w-2 shrink-0 rounded-full bg-cyan-500" aria-label="New updates" title="New updates from this agent" /> : null}
                {item.id === agent?.id ? <Check size={16} className="shrink-0 text-sky-600" strokeWidth={2.5} aria-hidden /> : null}
              </button>
            </li>
          ))}
          </ul>
        </div>, document.body
      ) : null}
    </div>
  );
}

export const chatTitle = (chat) => String(chat?.session_header || '').trim() || (chat?.session_kind === 'initiative' ? chat?.initiative_title || 'Initiative session' : 'New chat');

export const conversationOrigin = (chat) => chat?.session_kind === 'initiative'
  ? `Initiative${chat.initiative_title ? ` · ${chat.initiative_title}` : ''}`
  : 'Chat';

/** The person's chats with the selected agent, newest first, like ChatGPT's history. */
export function ChatsMenu({ liveSessionIds = new Set() }) {
  const panel = useAgentPanel();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [confirming, setConfirming] = useState(null);
  const [placement, setPlacement] = useState({ top: 16, left: 16 });
  const overlayRef = useRef(null);
  const close = () => {
    setOpen(false);
    setConfirming(null);
  };
  const ref = useDismiss(open, close, overlayRef);
  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const bounds = ref.current?.closest('[data-testid="agent-panel"]')?.getBoundingClientRect();
      const beside = bounds && bounds.left >= 316;
      setPlacement({
        top: Math.max(16, Math.min(bounds?.top || 16, window.innerHeight - 240)),
        left: beside ? bounds.left - 300 : Math.max(16, Math.min(bounds?.left || 16, window.innerWidth - 304)),
      });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open, ref]);
  const needle = query.trim().toLowerCase();
  const chats = (panel.chats || []).filter((chat) => !needle || `${chatTitle(chat)} ${conversationOrigin(chat)}`.toLowerCase().includes(needle));
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        title="Conversations"
        aria-label="Conversations"
        aria-expanded={open}
        data-testid="chats-menu"
        className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${open ? 'bg-sky-100 text-sky-600' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'}`}
      >
        <History size={15} />
      </button>
      {open ? createPortal(
        <div ref={overlayRef} role="dialog" aria-label="Conversations" style={placement} className="fixed z-[60] flex max-h-[min(60vh,calc(100dvh-32px))] w-72 max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-sm shadow-xl">
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
            <div>
              <h3 className="text-base font-semibold text-gray-800">Conversations</h3>
              <p className="text-xs text-gray-500">With {agentName(panel.agent)}</p>
            </div>
            <button type="button" onClick={close} aria-label="Close conversations" className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><X size={16} /></button>
          </div>
          <div className="p-1.5">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search conversations"
              aria-label="Search conversations"
              className="h-8 w-full rounded-lg border border-slate-200 px-2.5 text-sm focus:border-slate-400 focus:outline-none"
            />
          </div>
          {chats.length ? (
            <ul className="space-y-1 overflow-y-auto p-2" data-testid="chats-list">
              {chats.map((chat) => {
                const current = String(chat.id) === String(panel.sessionId);
                return (
                  <li key={chat.id} className={`group flex items-center gap-1 rounded-lg border transition-colors ${current ? 'border-sky-200 bg-sky-50' : 'border-gray-200 hover:border-sky-300 hover:bg-sky-50/40'}`}>
                    <button
                      type="button"
                      onClick={() => {
                        close();
                        if (!current) void panel.openChat(chat.id);
                      }}
                      className="min-w-0 flex-1 px-2.5 py-2 text-left"
                      aria-current={current ? 'true' : undefined}
                    >
                      <span className="flex items-center gap-1.5">
                        <span className="min-w-0 flex-1 truncate text-slate-800">{chatTitle(chat)}</span>
                        {liveSessionIds.has(String(chat.id)) ? <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-cyan-700"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-cyan-500" />Live</span> : null}
                      </span>
                      <span className="block truncate text-[11px] text-slate-500">{conversationOrigin(chat)}</span>
                      {chat.updated_at ? <span className="block text-[11px] text-slate-400">{relativeTime(chat.updated_at)}</span> : null}
                    </button>
                    {chat.can_delete !== false && chat.session_kind !== 'initiative' ? confirming === chat.id ? (
                      <button
                        type="button"
                        onClick={async () => {
                          await panel.deleteChat(chat.id);
                          setConfirming(null);
                        }}
                        className="mr-1 rounded-lg bg-rose-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-rose-700"
                      >
                        Delete
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirming(chat.id)}
                        title="Delete chat"
                        aria-label={`Delete ${chatTitle(chat)}`}
                        className="mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 size={13} />
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="px-3 py-3 text-xs text-slate-500">
              {needle ? 'No conversations match.' : `No earlier conversations with ${agentName(panel.agent)} yet.`}
            </p>
          )}
        </div>, document.body
      ) : null}
    </div>
  );
}

/**
 * The person's chat options: whether chats show how the agent works (its steps,
 * thinking and tools, the context it uses, its model and trace export). Off by
 * default, so a chat reads as a conversation.
 */
function ChatOptions() {
  const panel = useAgentPanel();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.type === 'keydown' ? event.key === 'Escape' : !ref.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);
  const name = agentName(panel.agent);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-label="Chat options"
        aria-expanded={open}
        title="Chat options"
        className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${open ? 'bg-gray-100 text-gray-700' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'}`}
      >
        <MoreHorizontal size={16} />
      </button>
      {open ? (
        <div className="absolute right-0 top-9 z-30 w-64 rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg">
          <label className="flex cursor-pointer items-start gap-2.5 rounded-lg px-2.5 py-2 hover:bg-gray-50">
            <input
              type="checkbox"
              checked={Boolean(panel.showWork)}
              onChange={(event) => panel.setShowWork(event.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300"
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-800">Show how {name} works</span>
              <span className="mt-0.5 block text-xs leading-snug text-gray-500">Steps, thinking and tools in each reply, plus the model and trace export.</span>
            </span>
          </label>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The same conversation body inside the floating frame or the optional dock.
 */
export default function AgentPanel({ onClose = null, floating = false, onToggleMount = null, fullscreen = false, onToggleFullscreen = null }) {
  const panel = useAgentPanel();
  const { isPreviewActive } = useSandboxSession();
  const activity = liveActivity(panel, panel.agent);
  const liveSessions = useLiveSessions(panel.agent?.id);
  const liveSessionIds = new Set(liveSessions.map((session) => String(session.session_id)));
  return (
    <section
      className={`flex h-full min-h-0 flex-col ${floating ? 'w-full' : 'shrink-0 border-l border-gray-200/80 bg-white'}`}
      style={floating ? undefined : { width: AGENT_PANEL_WIDTH }}
      data-testid="agent-panel"
      data-workspace-tour="chat"
    >
      <header className={`flex shrink-0 flex-wrap items-center justify-between gap-2 px-4 py-3 ${isPreviewActive
        ? 'border-b border-indigo-100 bg-gradient-to-r from-indigo-50 via-violet-50 to-indigo-50'
        : 'border-b border-gray-100 bg-gradient-to-b from-white to-slate-50/70'}`}>
        <div className="flex min-w-[120px] flex-1 items-center gap-2.5">
          <AgentAvatar ui={panel.agent?.ui} size="md" status={agentStatus(panel.agent, activity)} label={agentName(panel.agent)} />
          <div className="min-w-0 flex-1">
            <AgentSwitcher />
            <div className="mt-1.5 empty:hidden"><DraftPreviewControl /></div>
          </div>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-0.5">
          {panel.showWork && panel.canManage && panel.agent?.id ? (
            <AgentLlmQuickSelect key={panel.agent.id} agent={panel.agent} scopeDescription="Applies to this agent's conversations and work." />
          ) : null}
          {panel.messages?.length > 0 ? (
            <button
              type="button"
              onClick={() => panel.startNewChat()}
              title="New conversation"
              aria-label="New chat"
              data-testid="new-chat"
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              <SquarePen size={15} />
            </button>
          ) : null}
          {panel.showWork && panel.isAdmin ? (
            <button
              type="button"
              onClick={panel.exportConversationTrace}
              disabled={panel.isExportingConversation}
              aria-label="Export conversation trace"
              title={panel.exportConversationError || 'Export conversation trace (admin)'}
              className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${panel.isExportingConversation ? 'cursor-wait text-sky-500' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'}`}
            >
              <FileDown size={15} />
            </button>
          ) : null}
          <ChatsMenu liveSessionIds={liveSessionIds} />
          <ChatOptions />
          {onToggleMount ? (
            <button type="button" onClick={onToggleMount} aria-pressed={!floating} title={floating ? 'Pin to sidebar' : 'Unpin from sidebar'} className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${floating ? 'text-gray-400 hover:bg-gray-100 hover:text-gray-700' : 'bg-sky-100 text-sky-600'}`}>
              {floating ? <PanelRight size={15} /> : <PanelRightClose size={15} />}
            </button>
          ) : null}
          {onToggleFullscreen ? (
            <button type="button" onClick={onToggleFullscreen} title={fullscreen ? 'Exit full screen' : 'Full screen'} className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700">
              {fullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
          ) : null}
          {onClose ? (
            <>
              <span className="mx-0.5 h-5 w-px bg-gray-200" aria-hidden />
              <button type="button" onClick={onClose} title="Close" aria-label="Close conversation" className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500">
                <X size={17} />
              </button>
            </>
          ) : null}
        </div>
      </header>
      {activity?.waiting_on_you > 0 ? (
        <Link to="/app/home#needs-you" className="flex shrink-0 items-center gap-2 border-b border-amber-100 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100">
          <Bell size={13} />
          {activity.waiting_on_you === 1 ? '1 question or card is waiting on you' : `${activity.waiting_on_you} questions and cards are waiting on you`}
        </Link>
      ) : null}
      {activity?.unseen_updates > 0 ? (
        <Link to="/app/home#updates" className="flex shrink-0 items-center gap-2 border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100" data-testid="unseen-updates-bar">
          <span className="h-1.5 w-1.5 rounded-full bg-cyan-500" />
          {activity.unseen_updates === 1
            ? `1 new update from ${agentName(panel.agent)}`
            : `${activity.unseen_updates} new updates from ${agentName(panel.agent)}`}
        </Link>
      ) : null}
      <LiveElsewhere sessions={liveSessions} />
      <Conversation />
    </section>
  );
}

/**
 * Float by default on every page; remember the person's optional sidebar pin.
 */
export function AgentDock() {
  const panel = useAgentPanel();
  const isDesktop = useCanMountChat();
  const [mounted, setMounted] = useState(readMounted);
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    if (!panel.isPanelOpen) setFullscreen(false);
  }, [panel.isPanelOpen]);
  const toggleMount = () => {
    const next = !mounted;
    setMounted(next);
    setFullscreen(false);
    try { window.localStorage.setItem(MOUNT_STORAGE_KEY, String(next)); } catch { /* Optional preference. */ }
  };
  const close = () => panel.setIsPanelOpen(false);
  if (!panel.agents.length) return null;
  if (isDesktop && mounted && panel.isPanelOpen) {
    return <AgentPanel onClose={close} onToggleMount={toggleMount} />;
  }
  return (
    <FloatingConversation panel={panel} fullscreen={fullscreen}>
      <AgentPanel floating onClose={close} onToggleMount={isDesktop ? toggleMount : null} fullscreen={fullscreen} onToggleFullscreen={() => setFullscreen((value) => !value)} />
    </FloatingConversation>
  );
}
